<template>
  <div
    class="company-wall-hero"
    :class="{ 'company-wall-hero--two-column': hasExtra }"
  >
    <HeaderBenefits :benefits="benefits" />

    <div
      class="company-wall-hero__row"
      :class="{ 'company-wall-hero__row--two-column': hasExtra }"
    >
      <div class="company-wall-hero__content">
        <SHeading
          class="company-wall-hero__heading"
          :class="[
            { 'company-wall-hero__heading--centered': !hasExtra },
            headingSizeClass,
          ]"
          level="1"
          size="1"
        >
          {{ heading }}
        </SHeading>

        <div
          v-if="hasSubtitle"
          class="company-wall-hero__subtitle"
          :class="{ 'company-wall-hero__subtitle--centered': !hasExtra }"
        >
          <slot name="subtitle" />
        </div>

        <div
          class="company-wall-hero__form"
          :class="{ 'company-wall-hero__form--centered': !hasExtra }"
        >
          <CompanyWallHeroForm :form-submit-url="formUrl" />
        </div>
      </div>

      <div v-if="hasExtra" class="company-wall-hero__extra">
        <slot name="extra" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from "vue";

import { SHeading } from "@solvari/components";

import CompanyWallHeroForm from "~/features/company-wall/components/company-wall-hero/CompanyWallHeroForm.vue";
import HeaderBenefits from "~/shared/components/header/HeaderBenefits.vue";

const props = defineProps<{
  benefits: string[];
  dynamicLength?: boolean;
  formUrl: string | null;
  heading: string;
}>();

const slots = useSlots();
const hasExtra = computed(() => !!slots.extra);
const hasSubtitle = computed(() => !!slots.subtitle);

const headingSizeBreakpoints = [
  { maxLen: 42, className: null },
  { maxLen: 44, className: "company-wall-hero__heading--lg" },
  { maxLen: 46, className: "company-wall-hero__heading--md" },
  { maxLen: 50, className: "company-wall-hero__heading--sm" },
  { maxLen: Infinity, className: "company-wall-hero__heading--xs" },
] as const;

const headingSizeClass = computed(() => {
  if (!props.dynamicLength) {
    return null;
  }
  return headingSizeBreakpoints.find(
    ({ maxLen }) => props.heading.length <= maxLen,
  )!.className;
});
</script>

<style lang="postcss">
.company-wall-hero {
  @apply flex w-full flex-col gap-6;

  @screen 800 {
    @apply gap-8;
  }

  .header-benefits {
    @apply justify-center;
  }

  &--two-column .header-benefits {
    @screen 800 {
      @apply justify-start;
    }
  }

  &__row {
    @apply flex flex-col;

    &--two-column {
      @screen 1000 {
        @apply flex-row items-stretch;
      }
    }
  }

  &__content {
    @apply flex min-w-0 flex-1 flex-col text-center;
  }

  &__heading {
    @apply mb-6;

    @screen 700 {
      @apply mb-12;

      &:not(&--centered) {
        @apply text-left;
      }
    }

    &--lg.s-heading {
      font-size: 2rem;

      @screen 800 {
        @apply text-5xl;
      }

      @screen 1000 {
        @apply text-6xl;
      }
    }

    &--md.s-heading {
      font-size: 1.8rem;

      @screen 800 {
        @apply text-5xl;
      }

      @screen 1000 {
        font-size: 3.4rem;
      }
    }

    &--sm.s-heading {
      font-size: 1.8rem;

      @screen 800 {
        @apply text-5xl;
      }

      @screen 1000 {
        @apply text-5xl;
      }
    }

    &--xs.s-heading {
      font-size: 1.8rem;

      @screen 800 {
        font-size: 2.7rem;
      }

      @screen 1000 {
        @apply text-5xl;
      }
    }
  }

  &__subtitle {
    @apply hidden text-xl text-slate-600;

    &--centered {
      @apply mx-auto max-w-[700px];
    }

    @screen 700 {
      @apply mb-4 block;

      &:not(&--centered) {
        @apply text-left;
      }
    }
  }

  &__form {
    @apply w-full pb-8 font-light;

    &--centered {
      @apply mx-auto max-w-[700px];
    }

    @screen 1000 {
      @apply max-w-128;
    }
  }

  &__extra {
    @apply hidden min-w-52 shrink-0;

    @screen 1000 {
      @apply flex self-end;
    }
  }
}
</style>
